<template>
  <div
    class="publishing-image"
    :class="{ noSellables: !sellableImages.length || designImageOnly }"
  >
    <div v-if="showProgressBar">
      <PublishingProductsLoadingBar
        :time="1500"
        @loading-done="showProgressBar = false"
      />
    </div>
    <template v-else>
      <div v-if="!designImageOnly" class="random-image-wrapper">
        <RandomImage :image-urls="sellableImages" width="400" />
      </div>

      <div
        class="design-image-wrapper design-checked-background"
        :class="{ dark: composition.properties.darkBackground }"
      >
        <SprdImage class="design-image" :src="designImage" />
        <button
          class="dark-background-toggle"
          @click.prevent="toggleBackgroundColor"
        >
          <Icon
            icon="background-color"
            class="bg-toggle"
            :class="{
              'rotate-icon-180': composition.properties.darkBackground,
            }"
          />
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Composition } from '@spreadgroup/common-partner-lib/composition';
import compositionHelper from 'commons/composition/compositionHelper.ts';
import { useCompositionStore } from 'commons/stores/composition';

import SprdImage from 'commons/image/SprdImage.vue';
import RandomImage from 'src/app/components/randomImage/RandomImage.vue';
import PublishingProductsLoadingBar from 'src/app/partnerarea/publishing/detail/PublishingProductsLoadingBar.vue';

const compositionStore = useCompositionStore();

const props = defineProps<{
  composition: Composition;
  designImageOnly?: boolean;
}>();

const showProgressBar = ref(!props.designImageOnly);

const hasMultipleDesigns = compositionHelper.hasMultipleDesigns(
  props.composition,
);

const designImage = computed(() =>
  compositionHelper.getMainDesignImage(props.composition),
);

const sellableImages = computed(() =>
  hasMultipleDesigns
    ? compositionHelper.getAllActivatedSellableImages(props.composition)
    : compositionHelper.getDefaultActivatedSellableImages(props.composition),
);

const toggleBackgroundColor = () => {
  compositionStore.toggleBackgroundColor();
};
</script>

<style scoped lang="scss">
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.publishing-image {
  aspect-ratio: 1;
  flex: 1;
  width: 100%;
  margin: 0 24px 0 0;
  border: 1px solid $grey10;
  border-radius: $border-radius-medium;
  position: relative;
  overflow: hidden;

  & > div {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: $grey5;
  }

  &:hover,
  &.noSellables {
    .design-image-wrapper {
      display: block;
    }
    .random-image-wrapper {
      display: none;
    }
  }

  :deep(img) {
    width: 100%;
    height: 100%;
  }

  .dark-background-toggle {
    position: absolute;
    left: 4px;
    bottom: 4px;
    padding: 0;

    .icon {
      width: 24px;
      height: 24px;
    }
  }
  &.dark .dark-background-toggle {
    color: $grey5;
  }

  .design-image-wrapper {
    display: none;
    padding: 16px;

    &.dark .bg-toggle {
      color: $white;
    }
  }

  .random-image-wrapper {
    padding: 8px;
  }

  .design-image {
    display: flex;
  }
}
</style>
